import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TbBed } from "react-icons/tb";
import { MdRestaurantMenu } from "react-icons/md";
import { CorrectIcon } from "src/assets/Icons";
import type { BookingDetailResult, BookingDetailRoom } from "src/store/apis/Hotels";
import { getCancellationInfo, getMealTypeLabel } from "src/Modules/Hotels/utils/room";
import { formatNumber } from "src/utility";

type RoomGroup = { room: BookingDetailRoom; count: number };

// consecutive identical rooms are shown once with a count
const groupRooms = (rooms: BookingDetailRoom[]) =>
  rooms.reduce<RoomGroup[]>((groups, room) => {
    const last = groups[groups.length - 1];
    if (last && last.room.name?.[0] === room.name?.[0]) last.count += 1;
    else groups.push({ room, count: 1 });
    return groups;
  }, []);

const RoomField: FC<{ title: ReactNode; value: ReactNode }> = ({ title, value }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    <div className="text-font-subtle text-xs uppercase">{title}</div>
    <div className="text-font-default text-sm">{value}</div>
  </div>
);

const RoomGroupDetails: FC<RoomGroup & { currency: string }> = ({ room, count, currency }) => {
  const { t } = useTranslation();
  const roomName = room.name?.[0] ?? "";
  const cancellation = getCancellationInfo(room.cancelPolicies, room.isRefundable);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-4 md:flex-row">
        <RoomField
          title={t("BookingResult.roomSelection.roomType")}
          value={count > 1 ? t("BookingResult.stayChips.roomsCount", { count, room: roomName }) : roomName}
        />
        {room.inclusion && <RoomField title={t("BookingResult.roomSelection.inclusion")} value={room.inclusion} />}
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-bold">
        <span
          className={`inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5 ${
            cancellation.refundable ? "bg-background-success text-font-success" : "bg-background-danger text-font-danger"
          }`}
        >
          {cancellation.refundable ? t("Flights.listing.refundable") : t("Hotels.common.nonRefundable")}
        </span>

        {cancellation.hasFreeCancellation && (
          <span className="bg-background-success text-font-success inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5">
            <CorrectIcon className="text-font-success h-4 w-4 shrink-0" />
            {cancellation.freeCancelUntil
              ? t("Hotels.common.freeCancellationUntil", { date: cancellation.freeCancelUntil })
              : t("Hotels.common.freeCancellation")}
          </span>
        )}

        {cancellation.paidPolicies.map((policy, index) => (
          <span key={index} className="bg-background-danger text-font-danger inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5">
            {t(policy.chargeType === "Percentage" ? "Hotels.common.cancellationChargePercentage" : "Hotels.common.cancellationChargeAmount", {
              date: policy.fromDate,
              charge: policy.chargeType === "Percentage" ? policy.cancellationCharge : formatNumber(policy.cancellationCharge),
              currency: policy.currency ?? currency,
            })}
          </span>
        ))}

        {room.mealType && (
          <span className="text-font-informative bg-background-informative inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5">
            <MdRestaurantMenu className="h-4 w-4" />
            {getMealTypeLabel(room.mealType)}
          </span>
        )}

        {room.roomPromotion && (
          <span className="text-font-informative bg-background-informative inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5">
            <CorrectIcon className="h-4 w-4 shrink-0" />
            {room.roomPromotion}
          </span>
        )}
      </div>
    </div>
  );
};

const RoomSelection: FC<{ bookingDetail: BookingDetailResult }> = ({ bookingDetail }) => {
  const { t } = useTranslation();
  const roomGroups = groupRooms(bookingDetail.rooms);

  return (
    <div className="bg-background-default border-border-subtle flex w-full flex-col overflow-hidden rounded-sm border shadow-xs">
      <div className="bg-background-subtle border-border-subtle flex items-center gap-2 border-b px-4 py-3">
        <TbBed className="text-font-active size-5" />
        <div className="text-font-default text-base font-semibold">{t("BookingResult.roomSelection.title")}</div>
      </div>
      <div className="divide-border-subtle flex flex-col divide-y">
        {roomGroups.map((group, index) => (
          <RoomGroupDetails key={index} {...group} currency={bookingDetail.currency} />
        ))}
      </div>
    </div>
  );
};

export default RoomSelection;
